import React, { useEffect, useState } from 'react';
import { event } from 'nextjs-google-analytics';
import cx from 'classnames';
import Image from 'next/image';
import 'photoswipe/dist/photoswipe.css';
import { useRouter } from 'next/router';

import { BUTTON_TYPES } from 'constant';
import { PartResult, AfterMarketPartResult } from '@/types/api';
import useCart from '@/hooks/cart';

import Counter from '../Counter';
import PriceBox from '../PriceBox';
import Button from '../Button';
import styles from './PartInner.module.scss';
import Tag from '../Tag';
import AfterMarketOptions from './AfterMarketOptions';

const PartInner = ({
  title,
  dimensions: { width, height, length },
  weight,
  price,
  partNumber,
  mpn,
  photo,
  pageData,
  afterMarketParts,
}: PartResult & { afterMarketParts: AfterMarketPartResult } & {
  pageData: any;
}): JSX.Element => {
  const router = useRouter();
  const isMercedes = router.query.origin === 'oemstar.co.uk';
  const isToyota = router.query.origin === 'oemoval.co.uk';

  const { addItemToCart, isLoading } = useCart();

  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    if (!isLoading && isAddingToCart) setIsAddingToCart(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <div className="bg-white">
      <div className={cx(styles.container, 'grid mx-auto w-fit')}>
        <div
          className={cx(
            styles.photosContainer,
            'grid w-full gap-y-8 max-w-[588px] h-fit xl:mx-auto',
          )}
        >
          <div className="w-full aspect-square max-h-[588px]">
            <Image
              src={photo}
              alt={`${title ?? partNumber} image`}
              width={588}
              height={588}
              priority
              className="rounded-lg self-center h-full object-contain"
            />
          </div>
          {(isMercedes || isToyota) && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="/catalog"
              className="text-brand-700 hover:text-gold-hover underline"
            >
              Visit our online parts catalog
            </a>
          )}
          <div id="afterMarketOptions" className="hidden xl:block">
            <AfterMarketOptions
              isAddingToCart={isAddingToCart}
              setIsAddingToCart={setIsAddingToCart}
              afterMarketParts={afterMarketParts}
            />
          </div>
        </div>
        <div className={cx(styles.infoContainer, 'flex flex-col gap-y-8')}>
          <h1 className="font-medium text-2xl text-brand-100">
            {title ?? partNumber}
          </h1>
          <a
            className="text-brand-700 hover:text-gold-hover hidden xl:block"
            onClick={() => {
              event('Click', {
                category: 'Link',
                label: 'Aftermarket alternative',
              });
            }}
            style={{ marginTop: '-20px' }}
            href="#afterMarketOptions"
          >
            Interested in aftermarket alternative?
          </a>
          <a
            className="text-brand-700 hover:text-gold-hover lg:hidden"
            onClick={() => {
              event('Click', {
                category: 'Link',
                label: 'Aftermarket alternative',
              });
            }}
            style={{ marginTop: '-20px' }}
            href="#afterMarketOptionsMobile"
          >
            Interested in aftermarket alternative?
          </a>
          <hr className="border-gold-50 border-1" />
          {price ? (
            <PriceBox price={price} priceStyle="text-2xl" />
          ) : (
            <h1 className="font-bold text-2xl text-brand-100">
              Price unavailable, please contact us
            </h1>
          )}
          <div className="flex sm:flex-row flex-col gap-x-3 gap-y-3">
            <Tag
              icon="check"
              title="Part Available"
              iconColor="text-gray-muted"
              color="text-lime-700"
            />
            <Tag
              icon="check"
              title="Includes VAT"
              iconColor="text-gray-muted"
              color="text-lime-700"
            />
            <Tag
              icon="add"
              title="Excludes Shipping"
              iconColor="text-gray-muted"
              color="text-gray-muted"
              underlined
            />
          </div>
          <p className="text-sm font-medium">Stock is updated every 30 days</p>
          <ul className="list-disc ml-3 text-base">
            <li className="p-2">
              <div className="mr-1 font-medium">Part Number</div>
              <div>{mpn}</div>
            </li>
            <li className="p-2">
              <div className="mr-1 font-medium">Condition</div>
              <div>100% new and genuine</div>
            </li>
            <li className="p-2">
              <div className="mr-1 font-medium">Stock</div>
              <div>{pageData.stockText}</div>
            </li>
            <li className="p-2">
              <div className="mr-1 font-medium">Warranty</div>
              <div>{pageData.warrantyText}</div>
            </li>
            {weight?.value && (
              <li className="p-2">
                <div className="mr-1 font-medium">Weight</div>
                <div>
                  {weight?.value ?? 1} {weight?.units ?? 'kg'}
                </div>
              </li>
            )}
            {(length || width || height) && (
              <li>
                <ul className="flex flex-row">
                  <li className="p-2">
                    <div className="mr-1 font-medium">Length</div>
                    <div>{length ? `${length} cm` : 'Contact us'}</div>
                  </li>
                  <li className="p-2">
                    <div className="mr-1 font-medium">Width</div>
                    <div>{width ? `${width} cm` : 'Contact us'}</div>
                  </li>
                  <li className="p-2">
                    <div className="mr-1 font-medium">Height</div>
                    <div>{height ? `${height} cm` : 'Contact us'}</div>
                  </li>
                </ul>
              </li>
            )}
          </ul>
          {price ? (
            <div className="flex flex-row items-center gap-x-8">
              <Counter
                count={quantity}
                minCount={1}
                setCount={setQuantity}
                disabled={isAddingToCart}
              />
              <Button
                className={cx(styles.addToCartBtn, 'w-full h-[52px]')}
                type={BUTTON_TYPES.FILLED}
                onClick={() => {
                  setIsAddingToCart(true);
                  addItemToCart('part', quantity, partNumber);
                }}
                loading={isAddingToCart}
                testAttribute="add-to-cart"
              >
                Add to cart
              </Button>
            </div>
          ) : (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="mailto:parts@oemstock.co.uk"
              className="font-medium text-2xl text-gold-100"
            >
              Contact us for availability
            </a>
          )}
          <div id="afterMarketOptionsMobile" className="lg:hidden">
            <AfterMarketOptions
              isAddingToCart={isAddingToCart}
              setIsAddingToCart={setIsAddingToCart}
              afterMarketParts={afterMarketParts}
            />
          </div>
          <div className="lg:mt-8">
            <hr className="border-gray-900 border-1 mb-6" />
            <div className="pr-12">
              <div className="text-brand-100 text-lg font-medium mb-2">
                Delivery
              </div>
              <p className="font-normal text-base text-[#5A5A5A]">
                Usually, delivery will take 3 to 4 business days, depending on
                part you order and delivery address. But if ordered part is on
                Germany back order it usually takes 5-7 business days or longer.
                Please check our Delivery policy.
              </p>
            </div>
            <hr className="border-gray-900 border-1 mt-6" />
            <Image
              src="/images/payment-trust-badge.png"
              alt="Payment trust badge"
              width={0}
              height={0}
              sizes="100vw"
              className="mt-[24px] md:mt-[64px]"
              style={{ width: '100%', height: 'auto' }}
            />
            <Image
              src="/images/security.png"
              alt="Security tools"
              width={0}
              height={0}
              sizes="100vw"
              className="mt-[24px] md:mt-[64px]"
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartInner;
